import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Virtuoso } from 'react-virtuoso';
import { Fade, Popover, Typography } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';

import { PermModule } from '@playq/octopus2-auth';

import { useModal } from '/hooks';
import { appToolkit } from '/store';

import { SearchList, Row } from './SearchList';
import { ISearchEntity, ISelectSearchList } from './types';
import { componentMap, removeSpaces, removeVersionWithDot } from './helpers';

const defaultDimensions = { height: 200, width: 250, itemSize: 35 };
/**
 *
 * @template T - The type of the value in the item object.
 * @property {boolean} [action=false] - Whether to show action buttons or a function to determine action visibility. Important: add onCopy or onDelete props
 * @property {boolean} [prefixSearch=false] - When true, enables full-term matching for numbers or keywords like '1232' or 'answer' and implement trie alhorithm but elements must be more 10000.
 * @property {string} [view='default'] - The visual style of the component ('default', 'version', or 'link').
 * @property {object} [dimensions] - Dimensions for the virtualized list.
 * @property {string} [error] - Error message to display in Row component.
 * @property {ReacNode} [ViewRender] - Implement custom View Component.
 * @property {ReacNode} [InputRender] - Implement custom Search Component.
 * @property {ReacNode} [children] - add custom content before list of items.
 */
export const SelectSearchList = <T,>({
  action = false,
  prefixSearch = false,
  view = 'default',
  hiddenSearch = false,
  fullWidth,
  disabled = false,
  showAvatar,
  dimensions = defaultDimensions,
  itemList,
  label,
  placeholder = 'Search...',
  onItemSelect,
  onCopy,
  onDelete,
  error,
  children,
  ViewRender,
  SearchRender,
}: ISelectSearchList<T>) => {
  const { anchorEl, isOpenAnchorEl, openAnchorEl, closeAnchorEl } = useModal();
  const searchInputRef = useRef<HTMLDivElement>(null);
  const [popoverWidth, setPopoverWidth] = useState<number>(defaultDimensions.width);

  useEffect(() => {
    const viewElement = searchInputRef.current;
    if (!viewElement) return;

    const observer = new ResizeObserver(([entry]) => {
      const newWidth = entry.target.getBoundingClientRect().width;
      const finalWidth = Math.max(newWidth, dimensions.width);
      setPopoverWidth(finalWidth);
    });

    observer.observe(viewElement);
    return () => observer.disconnect();
  }, [dimensions.width]);

  const { canWrite, canWriteLive } = useSelector(appToolkit.selectors.moduleAccess(PermModule.AnalyticsSegmentation));
  const readonly = !canWrite && !canWriteLive;

  const listHeight = useMemo(
    () => Math.min(dimensions.height, dimensions.itemSize * itemList.length),
    [dimensions.height, dimensions.itemSize, itemList.length]
  );
  const handleItemClick = useCallback(
    (item: ISearchEntity<T>, index: number) => {
      onItemSelect?.(item, index);
      closeAnchorEl();
    },
    [onItemSelect, closeAnchorEl]
  );
  const handleItemCopy = useCallback(
    (e: React.MouseEvent, value: ISearchEntity<T>['value']) => {
      e.stopPropagation();
      onCopy?.(value);
    },
    [onCopy]
  );

  const handleItemDelete = useCallback(
    (e: React.MouseEvent, value: T) => {
      e.stopPropagation();
      onDelete?.(value);
    },
    [onDelete]
  );
  const isItemSelected = useCallback(
    (item: ISearchEntity<T>) => {
      if (typeof label !== 'string') return false;
      const normalizedItemLabel = removeSpaces(item.label);
      const normalizedLabel = removeSpaces(label);
      if (view === 'version') {
        return removeVersionWithDot(normalizedLabel) === normalizedItemLabel;
      }
      return normalizedItemLabel === normalizedLabel || item.value === normalizedLabel;
    },
    [label, view]
  );
  const ViewSelect = componentMap[view];

  const viewSelectProps = {
    ref: searchInputRef,
    component: 'button',
    type: view !== 'link' ? 'button' : undefined,
    $isOpen: isOpenAnchorEl,
    'data-testid': 'select-item-btn',
  };
  const virtuosoStyle = {
    height: listHeight,
    width: fullWidth ? '100%' : dimensions.width,
  };

  return (
    <>
      {ViewRender ? (
        ViewRender({ viewSelectProps, openAnchorEl })
      ) : (
        <ViewSelect
          {...viewSelectProps}
          disabled={disabled}
          onClick={openAnchorEl}
          $width={view !== 'link' ? (fullWidth ?? 200) : '100%'}
        >
          {typeof label !== 'string' ? label : <Typography noWrap>{label}</Typography>}
          {view !== 'link' && <ArrowDropDown />}
        </ViewSelect>
      )}
      <Popover
        id={anchorEl ? 'simple-popover' : 'null'}
        open={isOpenAnchorEl}
        anchorEl={anchorEl}
        slotProps={{
          paper: {
            sx: { width: popoverWidth },
          },
        }}
        onClose={closeAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        TransitionComponent={Fade}
        keepMounted={false}
      >
        {hiddenSearch ? (
          <>
            {children}
            <Virtuoso
              style={virtuosoStyle}
              defaultItemHeight={dimensions.itemSize}
              totalCount={itemList.length}
              computeItemKey={(index) => `item-${itemList[index].label}`}
              itemContent={(index) => {
                return (
                  <Row
                    readonly={readonly}
                    showAvatar={showAvatar}
                    index={index}
                    selectedItem={isItemSelected(itemList[index])}
                    currentItem={itemList[index]}
                    onItemClick={handleItemClick}
                    height={dimensions.itemSize}
                  />
                );
              }}
            />
          </>
        ) : (
          <SearchList
            SearchRender={SearchRender !== undefined ? (props) => SearchRender({ ...props, closeAnchorEl }) : undefined}
            prefixSearch={prefixSearch}
            placeholder={placeholder}
            itemList={itemList}
            renderList={(filteredList) => (
              <>
                {children}
                <Virtuoso
                  style={virtuosoStyle}
                  defaultItemHeight={dimensions.itemSize}
                  totalCount={filteredList.length}
                  computeItemKey={(index) => `item-${filteredList[index].label}`}
                  itemContent={(index) => {
                    const currentItem = filteredList[index];

                    const actionValue =
                      typeof action === 'function' && currentItem.value !== undefined
                        ? action(currentItem.value)
                        : action;
                    return (
                      <Row
                        readonly={readonly}
                        showAvatar={showAvatar}
                        action={actionValue}
                        index={index}
                        selectedItem={isItemSelected(currentItem)}
                        currentItem={currentItem}
                        onItemClick={handleItemClick}
                        onItemCopy={onCopy && handleItemCopy}
                        onItemDelete={onDelete && handleItemDelete}
                        error={error}
                        height={dimensions.itemSize}
                      />
                    );
                  }}
                />
              </>
            )}
          />
        )}
      </Popover>
    </>
  );
};
